import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Bell,
  BedDouble,
  Building2,
  Check,
  ChevronDown,
  CircleHelp,
  Inbox,
  LayoutDashboard,
  Plane,
  ScanLine,
  ShieldCheck,
  Users,
  Wallet,
} from "lucide-react";
import BrandLogo from "../BrandLogo";
import WorkspaceScene from "./WorkspaceScene";

const SECTIONS = [
  {
    id: "brief",
    label: "Overview",
    icon: LayoutDashboard,
    title: "Review the event brief.",
    detail:
      "Barcelona, 140 people, four days. See what has been sourced so far.",
  },
  {
    id: "venues",
    label: "Venues",
    icon: Building2,
    title: "Three venues to consider.",
    detail: "Review capacity, location and hire costs before you choose.",
  },
  {
    id: "quotes",
    label: "Vendors",
    icon: Inbox,
    title: "Review supplier replies.",
    detail: "Read replies, negotiated rates and the terms behind each quote.",
  },
  {
    id: "flights",
    label: "Flights",
    icon: Plane,
    title: "Work out how everyone gets there.",
    detail:
      "Price flights from each departure city before booking.",
  },
  {
    id: "hotels",
    label: "Hotels",
    icon: BedDouble,
    title: "Choose the rooms. Book in Comtev.",
    detail: "Review room rates and cancellation terms, then get approval to book.",
  },
  {
    id: "budget",
    label: "Budget",
    icon: Wallet,
    title: "See the budget before you commit.",
    detail: "Switch between venue and travel options to see the total cost.",
  },
  {
    id: "signoff",
    label: "Approvals",
    icon: ShieldCheck,
    title: "Approve the travel budget.",
    detail: "Review £59,942 for flights and hotels before approving the spend.",
  },
  {
    id: "guests",
    label: "Guests",
    icon: Users,
    title: "Collect the remaining guest names.",
    detail:
      "131 names collected. Comtev follows up with team leads for the remaining 9.",
  },
  {
    id: "doors",
    label: "Check-in",
    icon: ScanLine,
    title: "Check guests in at the door.",
    detail: "Scan guest passes at the door and see arrivals as they happen.",
  },
];

const EventWorkspace: React.FC = () => {
  const [active, setActive] = useState(0);
  const reduced = useReducedMotion();
  const section = SECTIONS[active];
  const navigate = (index: number, focus = false) => {
    setActive(index);
    if (focus)
      document.getElementById(`workspace-tab-${SECTIONS[index].id}`)?.focus();
  };

  return (
    <div className="event-explorer">
      <div className="ew-window">
        <div className="ew-titlebar">
          <span className="ew-window-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span>
            <ShieldCheck size={11} aria-hidden="true" /> Comtev / Northstar /
            Barcelona kickoff
          </span>
          <span className="ew-sample-label">SAMPLE WORKSPACE</span>
        </div>
        <div className="ew-layout">
          <aside className="ew-sidebar">
            <BrandLogo surface="dark" width={110} height={24} />
            <div className="ew-org">
              <span className="ew-org-mark">N</span>
              <span>
                Northstar<span>Company workspace</span>
              </span>
              <ChevronDown size={13} aria-hidden="true" />
            </div>
            <p className="ew-nav-label">BARCELONA KICKOFF</p>
            <div
              role="tablist"
              aria-label="Explore the sample event"
              aria-orientation="vertical"
              className="ew-nav"
            >
              {SECTIONS.map((item, index) => (
                <button
                  type="button"
                  key={item.id}
                  id={`workspace-tab-${item.id}`}
                  role="tab"
                  aria-selected={active === index}
                  aria-controls="workspace-panel"
                  tabIndex={active === index ? 0 : -1}
                  onClick={() => navigate(index)}
                  onKeyDown={(event) => {
                    const keys = [
                      "ArrowDown",
                      "ArrowRight",
                      "ArrowUp",
                      "ArrowLeft",
                      "Home",
                      "End",
                    ];
                    if (!keys.includes(event.key)) return;
                    event.preventDefault();
                    const next =
                      event.key === "Home"
                        ? 0
                        : event.key === "End"
                          ? SECTIONS.length - 1
                          : (index +
                              (["ArrowDown", "ArrowRight"].includes(event.key)
                                ? 1
                                : -1) +
                              SECTIONS.length) %
                            SECTIONS.length;
                    navigate(next, true);
                  }}
                >
                  <item.icon size={15} strokeWidth={1.6} aria-hidden="true" />
                  <span>{item.label}</span>
                  {item.id === "signoff" && <small>1</small>}
                </button>
              ))}
            </div>
            <div className="ew-sidebar-bottom">
              <span>
                <CircleHelp size={14} aria-hidden="true" /> Activity history
              </span>
              <div>
                <span className="ew-avatar ew-avatar--warm">HR</span>
                <span>
                  Hannah Reid<small>Event lead</small>
                </span>
              </div>
            </div>
          </aside>
          <div className="ew-main">
            <header className="ew-eventbar">
              <span>
                Events <span>/</span> <b>Barcelona kickoff</b>
              </span>
              <div className="ew-collaborators">
                <span className="ew-avatar">HR</span>
                <span className="ew-avatar ew-avatar--rose">JM</span>
                <span className="ew-avatar ew-avatar--sage">AS</span>
                <Bell size={15} aria-hidden="true" />
              </div>
            </header>
            <div className="ew-event-heading">
              <div>
                <p>9—12 MARCH 2027 · BARCELONA</p>
                <h2>Barcelona company kickoff</h2>
                <span>
                  Company kickoff <i /> 140 people <i /> 3 nights
                </span>
              </div>
              <span className="ew-event-state">
                <span /> In planning
              </span>
            </div>
            <div
              id="workspace-panel"
              role="tabpanel"
              tabIndex={0}
              aria-labelledby={`workspace-tab-${section.id}`}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={section.id}
                  initial={reduced ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.18 }}
                >
                  <WorkspaceScene
                    section={section.id}
                    onNavigate={(id) => {
                      const index = SECTIONS.findIndex(
                        (item) => item.id === id,
                      );
                      if (index >= 0) navigate(index, true);
                    }}
                  />
                </motion.div>
              </AnimatePresence>
            </div>
            <footer className="ew-statusbar">
              <span>
                <Check size={12} aria-hidden="true" /> Sample event
              </span>
              <span>Illustrative data · No messages sent or bookings made</span>
            </footer>
          </div>
        </div>
      </div>
      <div className="event-explorer-caption">
        <div aria-live="polite">
          <p>
            {String(active + 1).padStart(2, "0")} <span>/ {String(SECTIONS.length).padStart(2, "0")}</span>
          </p>
          <div>
            <h3>{section.title}</h3>
            <span>{section.detail}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate((active + 1) % SECTIONS.length, true)}
          aria-label={`Explore ${SECTIONS[(active + 1) % SECTIONS.length].label}`}
        >
          {active === SECTIONS.length - 1
            ? "Back to overview"
            : `Next: ${SECTIONS[active + 1].label}`}
          <ArrowRight size={17} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default EventWorkspace;
